import { FormControl, InputLabel, MenuItem, Select, SelectChangeEvent } from '@mui/material';
import { useEffect } from 'react';
import useListSeasonsQuery from '../hooks/useListSeasonsQuery';

type ListSeasonsProps = {
  value: string;
  onChange: (seasonId: string) => void;
  leagueId: number;
  size?: 'small' | 'medium';
};

// No default for `size`: an explicit value passed to FormControl outranks the
// theme's MuiFormControl defaultProps, so defaulting it here to 'medium' opted
// every caller out of the app-wide field scale. Left undefined, the theme wins.
export const ListSeasons = ({ leagueId, value, onChange, size }: ListSeasonsProps) => {
  const listSeasonsQuery = useListSeasonsQuery(leagueId);
  const seasons = listSeasonsQuery.data?.seasons;

  // `value` is the only copy of the selection — this component mirrors nothing.
  // It used to hold its own state behind a one-shot `hasInitialized` latch, and
  // that lost the race on an org/league switch: with the seasons query already
  // cached, the child auto-selected first, then the parent's reset effect (which
  // React runs AFTER the child's) pushed the empty sentinel back down. The latch
  // meant auto-select never fired again — a populated dropdown with nothing
  // selected and no games loaded, only when the cache happened to be warm.
  const selectedSeason = seasons?.find((season) => season.id.toString() === value);

  // Stated as an invariant — "the selection must be a season this league has" —
  // rather than an initialization step. It is idempotent, so it no longer
  // matters who writes last: a clobbered selection self-corrects on the next
  // pass instead of latching empty.
  useEffect(() => {
    if (!seasons?.length || selectedSeason) return;
    const defaultSeasonId = listSeasonsQuery.data?.currentSeasonId;
    if (defaultSeasonId) onChange(defaultSeasonId.toString());
  }, [seasons, selectedSeason, listSeasonsQuery.data?.currentSeasonId, onChange]);

  const handleSeasonChange = (event: SelectChangeEvent<string>) => {
    onChange(event.target.value);
  };

  return (
    <FormControl fullWidth size={size}>
      <InputLabel>Season</InputLabel>

      <Select
        labelId="season-select-label"
        id="season-select"
        // '' rather than the caller's sentinel while the correction is in
        // flight: MUI warns and blanks the field for an out-of-range value.
        value={selectedSeason ? value : ''}
        label="Season"
        onChange={handleSeasonChange}
      >
        {seasons?.map((season) => (
          // String, not season.id — the value comes back through onChange
          // verbatim, and callers compare it with `season.id.toString()`.
          <MenuItem key={season.id} value={season.id.toString()}>
            {season.name} - {season.seasonType}
          </MenuItem>
        ))}
      </Select>
    </FormControl>
  );
};

export default ListSeasons;
